import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n/i18n';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const LanguageSelector = ({ variant = 'header' }) => {
  const { i18n, t } = useTranslation();
  const { token, user, setUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode) => {
    // Change language in i18n
    i18n.changeLanguage(langCode);
    setIsOpen(false);

    // Save to database if user is logged in
    if (token && user) {
      try {
        await axios.put(`${API}/user/language`, 
          { language: langCode },
          { 
            headers: { Authorization: `Bearer ${token}` },
             
          }
        );
        // Update local user state
        if (setUser) {
          setUser({ ...user, language: langCode });
        }
        toast.success(t('settings.languageUpdated'));
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }

    // Handle RTL for Arabic
    if (langCode === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = langCode;
    }
  };

  // Header variant (compact dropdown)
  if (variant === 'header') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 text-[#71717A] hover:text-white transition-colors"
          data-testid="language-selector-header"
        >
          <Globe size={18} />
          <span className="text-sm hidden md:inline">{currentLanguage.flag}</span>
          <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-[#0A0A0A] border border-[#1C1C1C] shadow-xl z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-[#1C1C1C] transition-colors ${
                  currentLanguage.code === lang.code ? 'text-[#D4AF37]' : 'text-white'
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="flex items-center gap-3">
                  <span>{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                </span>
                {currentLanguage.code === lang.code && (
                  <Check size={16} className="text-[#D4AF37]" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Settings variant (full list)
  if (variant === 'settings') {
    return (
      <div className="space-y-4">
        <h3 className="text-white font-serif text-lg">{t('settings.selectLanguage')}</h3>
        <div className="grid gap-3">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center justify-between px-5 py-4 border transition-all ${
                currentLanguage.code === lang.code 
                  ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                  : 'border-[#27272A] hover:border-[#D4AF37]/50 bg-[#0A0A0A]'
              }`}
              data-testid={`lang-settings-${lang.code}`}
            >
              <span className="flex items-center gap-4">
                <span className="text-2xl">{lang.flag}</span>
                <span className="text-left">
                  <span className="block text-white">{lang.nativeName}</span>
                  <span className="block text-[#52525B] text-sm">{lang.name}</span>
                </span>
              </span>
              {currentLanguage.code === lang.code && (
                <div className="w-6 h-6 bg-[#D4AF37] flex items-center justify-center">
                  <Check size={14} className="text-black" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default LanguageSelector;
